import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  Field,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  Tabs,
  Textarea,
  type Tab,
} from '@themes/aegix';
import { toaster } from '../../ui/toaster';

/**
 * Upload-or-paste dialog for SAML rotation certificates. Mirrors the
 * Upload / Paste tabs in `ImportMetadataDialog` but operates on raw cert
 * text rather than full SAML federation metadata.
 *
 * Returns the cert text via `onApply`. The caller is responsible for
 * calling the backend (PEM markers are stripped server-side per the
 * existing `addSamlRotationCert` contract).
 */

export interface ImportCertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (cert: string) => void | Promise<void>;
  /** Optional dialog title — defaults to "Import certificate". */
  title?: string;
  /** Disable the Import action while an upstream mutation is pending. */
  pending?: boolean;
}

type TabId = 'upload' | 'paste';

const TABS: Tab[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'paste', label: 'Paste' },
];

export function ImportCertDialog({
  isOpen,
  onClose,
  onApply,
  title = 'Import certificate',
  pending = false,
}: ImportCertDialogProps) {
  const [activeTab, setActiveTab] = useState<TabId>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [certText, setCertText] = useState<string>('');

  // Reset on open so a re-open doesn't show stale input.
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('upload');
    setFileName(null);
    setCertText('');
  }, [isOpen]);

  const handleFile = async (file: File) => {
    try {
      const text = await file.text();
      setFileName(file.name);
      setCertText(text);
    } catch (err) {
      toaster.create({
        title: 'File read failed',
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    }
  };

  const canApply = certText.trim().length > 0 && !pending;

  const handleApply = async () => {
    if (!canApply) return;
    await onApply(certText);
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="640px"
            data-e2e="sso-import-cert-dialog"
          >
            <Dialog.Header>
              <Dialog.Title>{title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                <Tabs
                  tabs={TABS}
                  activeTab={activeTab}
                  onChange={(id) => setActiveTab(id as TabId)}
                />

                {activeTab === 'upload' && (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label htmlFor="import-cert-file">
                        Certificate file
                      </Field.Label>
                      <Field.HelperText
                        fontSize="xs"
                        color="text.secondary"
                        mb={2}
                      >
                        Pick the IdP's signing certificate (.pem / .cer /
                        .crt). PEM markers are kept verbatim — the backend
                        strips them.
                      </Field.HelperText>
                      <HStack gap={3}>
                        <ButtonComponent
                          type="button"
                          visual="secondary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={pending}
                        >
                          {fileName ? 'Change file' : 'Choose file…'}
                        </ButtonComponent>
                        <Text
                          fontSize="sm"
                          color={fileName ? 'text.primary' : 'text.secondary'}
                          wordBreak="break-all"
                        >
                          {fileName ?? 'No file selected'}
                        </Text>
                      </HStack>
                      <input
                        ref={fileInputRef}
                        id="import-cert-file"
                        type="file"
                        accept=".pem,.cer,.crt,.txt,application/x-pem-file"
                        hidden
                        data-e2e="sso-import-cert-file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleFile(file);
                        }}
                      />
                    </Field.Root>
                  </VStack>
                )}

                {activeTab === 'paste' && (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label htmlFor="import-cert-text">
                        Certificate
                      </Field.Label>
                      <Field.HelperText
                        fontSize="xs"
                        color="text.secondary"
                        mb={2}
                      >
                        Paste the X.509 certificate (PEM markers OK; they're
                        stripped automatically).
                      </Field.HelperText>
                      <Textarea
                        id="import-cert-text"
                        data-e2e="sso-import-cert-text"
                        fontFamily="mono"
                        fontSize="xs"
                        rows={8}
                        placeholder="-----BEGIN CERTIFICATE-----..."
                        value={certText}
                        onChange={(e) => setCertText(e.target.value)}
                      />
                    </Field.Root>
                  </VStack>
                )}

                {certText.trim() && (
                  <Box
                    borderWidth="1px"
                    borderColor="border.secondary"
                    borderRadius="md"
                    p={3}
                    data-e2e="sso-import-cert-preview"
                  >
                    <Text
                      textStyle="captionEmphasized"
                      color="text.secondary"
                      mb={1}
                    >
                      Preview
                    </Text>
                    <Text
                      fontFamily="mono"
                      fontSize="xs"
                      color="text.primary"
                      wordBreak="break-all"
                    >
                      {`${certText.trim().slice(0, 120)}…`}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <ButtonComponent visual="secondary" onClick={onClose}>
                  Cancel
                </ButtonComponent>
                <ButtonComponent
                  visual="primary"
                  onClick={() => void handleApply()}
                  disabled={!canApply}
                  data-e2e="sso-import-cert-apply"
                >
                  {pending ? 'Importing…' : 'Import'}
                </ButtonComponent>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default ImportCertDialog;
